import { supabase } from './supabase'
import { formatCents } from './money'

/**
 * The plain-language story of one thing.
 *
 * spec.md asks for "a plain-language timeline on every item", and the append-
 * only ledger is what makes it possible — but only if someone turns the rows
 * into sentences. An activity log nobody can read is an audit trail, not a
 * history, and audit trails are for lawyers.
 *
 * Two sources, one story: `activity_log` knows what people and the pipeline
 * did, `payment_events` knows what happened to the money. They are merged and
 * sorted, because "betaald gemarkeerd" and "teruggenomen" only make sense next
 * to each other.
 *
 * The rendering is a pure function so the wording can be tested — this is the
 * screen someone reads a year later to work out what on earth happened.
 */

export interface HistoryEntry {
  at: string
  line: string
  /** Set when the entry is a correction rather than a step forward. */
  undone?: boolean
}

const ACTIONS: Record<string, (d: Record<string, unknown>) => string> = {
  'document.sent': (d) => {
    const pages = Number(d.pages ?? 0)
    return pages > 1 ? `Verstuurd — ${pages} pagina's` : 'Verstuurd'
  },
  'document.extracted': (d) =>
    d.kind === 'letter' ? 'Gelezen — het is een brief' : 'Gelezen door de computer',
  'document.failed': () => 'Kon niet gelezen worden',
  'obligation.verified': () => 'Nagekeken en goedgekeurd',
  'payment.recorded': (d) => `Betaald gemarkeerd — ${cents(d.amount_cents)}`,
  'payment.voided': (d) =>
    d.reason ? `Teruggenomen — ${String(d.reason)}` : 'Teruggenomen',
  'payment.dd_confirmed': (d) => `Domiciliëring geïnd — ${cents(d.amount_cents)}`,
  'payment.dd_failed': () => 'Domiciliëring mislukt',
  'payment.dispute_opened': () => 'Betwist',
  'payment.dispute_resolved': () => 'Betwisting opgelost',
  'payment.credit_note': (d) => `Creditnota — ${cents(d.amount_cents)}`,
  'payment.refund': (d) => `Terugbetaling — ${cents(d.amount_cents)}`,
  'payment.write_off': (d) => `Afgeboekt — ${cents(d.amount_cents)}`,
  'payment.cancelled': () => 'Geannuleerd',
  'instalment.planned': (d) =>
    d.planned_date ? `Gepland voor ${date(String(d.planned_date))}` : 'Planning verwijderd',
  'recurring.created': () => 'Ingesteld als vaste kost',
  'recurring.materialised': (d) => `${Number(d.created ?? 0)} keer vooruit gezet`,
  'recurring.matched': (d) => {
    const expected = cents(d.expected_cents)
    const actual = cents(d.actual_cents)
    return `Factuur gekoppeld aan de verwachting — geschat ${expected}, werd ${actual}`
  },
  'obligation.created_manually': () => 'Met de hand toegevoegd',
  'payment.proof_added': () => 'Bewijs toegevoegd',
  'document.corrected': () => 'Nagekeken en verbeterd',
  'document.confirmed': () => 'Nagekeken en goedgekeurd',
  'trash.moved': () => 'In de prullenbak gegooid',
  'trash.restored': () => 'Teruggehaald uit de prullenbak',
  'todo.created': () => 'Toegevoegd',
}

/** Never show a raw action name. An unknown one is skipped, not printed. */
export function describeAction(
  action: string,
  detail: Record<string, unknown>,
): string | null {
  const render = ACTIONS[action]
  return render ? render(detail) : null
}

export async function loadHistory(
  entityType: string,
  entityIds: string[],
): Promise<HistoryEntry[]> {
  if (entityIds.length === 0) return []

  const { data, error } = await supabase
    .from('activity_log')
    .select('action, detail, created_at')
    .eq('entity_type', entityType)
    .in('entity_id', entityIds)
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error

  const entries: (HistoryEntry | null)[] = (data ?? []).map((row) => {
    const line = describeAction(
      row.action as string,
      (row.detail as Record<string, unknown>) ?? {},
    )
    return line
      ? {
          at: row.created_at as string,
          line,
          undone: (row.action as string) === 'payment.voided',
        }
      : null
  })

  return entries.filter((entry): entry is HistoryEntry => entry !== null)
}

function cents(value: unknown): string {
  const n = Number(value)
  return Number.isFinite(n) ? formatCents(n) : '—'
}

function date(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'long' }).format(
    new Date(`${iso}T00:00:00`),
  )
}

/** "vandaag", "gisteren", or a real date. Relative only where it helps. */
export function whenLabel(iso: string, now = new Date()): string {
  const then = new Date(iso)
  const days = Math.floor(
    (startOfDay(now).getTime() - startOfDay(then).getTime()) / 86400000,
  )
  const time = new Intl.DateTimeFormat('nl-BE', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(then)

  if (days === 0) return `vandaag ${time}`
  if (days === 1) return `gisteren ${time}`
  if (days < 7) return `${days} dagen geleden`
  return new Intl.DateTimeFormat('nl-BE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(then)
}

function startOfDay(d: Date): Date {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy
}
